import { useEffect, useState } from 'react';
import { useRender, useT } from '../context';
import type { MenuNode } from '../types';
import { SmartLink } from '../components/common';

function LanguageSwitcher() {
  const { site, page, lang } = useRender();
  const t = useT();
  const langs = site?.languages ?? [];
  if (langs.length < 2) return null;
  return (
    <li className="kfz-lang">
      <nav aria-label={t('language')}>
        {langs.map((l) => {
          const alt = page?.alternates.find((a) => a.language === l.language);
          const href = alt ? new URL(alt.url).pathname : l.url;
          return (
            <a key={l.language} href={href} hrefLang={l.language} lang={l.language} aria-current={l.language === lang ? 'true' : undefined}
              className={l.language === lang ? 'active' : undefined}>
              {l.language.toUpperCase()}
            </a>
          );
        })}
      </nav>
    </li>
  );
}

function isCurrent(node: MenuNode, path?: string): boolean {
  return !!path && (node.href === path || node.children.some((c) => isCurrent(c, path)));
}

export function Header() {
  const { site, menus, page, lang } = useRender();
  const t = useT();
  const [sticky, setSticky] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [openDesktop, setOpenDesktop] = useState<string | null>(null);
  const [openMobile, setOpenMobile] = useState<Record<string, boolean>>({});
  const main = menus.main ?? [];
  const home = lang === 'de' ? '/' : `/${lang}`;

  useEffect(() => {
    const onScroll = () => setSticky(window.scrollY > 100);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!site) return null;

  return (
    <>
      <div className="top-bar">
        <div className="container">
          <div className="clearfix">
            <div className="top-left top-links">
              <ul className="clearfix">
                <li>
                  <SmartLink href={site.phoneHref} tracking="phone" trackingLocation="top_left_header">
                    <i className="fa fa-phone" aria-hidden="true" />
                    {site.phoneDisplay}
                  </SmartLink>
                </li>
                <li>
                  <SmartLink href={`mailto:${site.emailPrimary}`} tracking="email" trackingLocation="top_left_header">
                    <i className="fa fa-envelope" aria-hidden="true" />
                    {site.emailPrimary}
                  </SmartLink>
                </li>
              </ul>
            </div>
            <div className="affa" />
            <div className="top-right">
              <ul className="social-icon">
                {site.instagramUrl ? (
                  <li>
                    <a href={site.instagramUrl} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                      <i className="fa fa-instagram" aria-hidden="true" />
                    </a>
                  </li>
                ) : null}
                <LanguageSwitcher />
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="header-upper">
        <div className="container">
          <div className="clearfix">
            <div className="pull-left logo-outer">
              <div className="logo-box">
                <a href={home}>
                  {site.logo ? (
                    <img src={site.logo.url} alt={site.logo.alt || site.companyName} width={site.logo.width ?? undefined} height={site.logo.height ?? undefined} />
                  ) : (
                    site.companyName
                  )}
                </a>
              </div>
            </div>
            <div className="pull-right upper-right clearfix header-contact-area">
              <div className="upper-column info-box">
                <div className="icon-box">
                  <i className="fa fa-phone" aria-hidden="true" />
                </div>
                <ul>
                  <li>
                    <strong>{t('phone')}</strong>
                  </li>
                  <li>
                    <SmartLink href={site.phoneHref} tracking="phone" trackingLocation="header_phone_text">
                      {site.phoneDisplay}
                    </SmartLink>
                  </li>
                </ul>
              </div>
              <div className="upper-column info-box">
                <div className="icon-box">
                  <i className="fa fa-map-marker" aria-hidden="true" />
                </div>
                <ul>
                  <li>
                    <strong>{t('address')}</strong>
                  </li>
                  <li>
                    {site.addressMapsUrl ? (
                      <SmartLink href={site.addressMapsUrl} tracking="maps" trackingLocation="header_address_text">
                        {site.addressLine}
                      </SmartLink>
                    ) : (
                      site.addressLine
                    )}
                  </li>
                </ul>
              </div>
              <div className="upper-column info-box">
                <div className="icon-box">
                  <i className="fa fa-clock-o" aria-hidden="true" />
                </div>
                <ul>
                  <li>
                    <strong>{t('workingHours')}</strong>
                  </li>
                  <li>{site.openingHoursShort}</li>
                </ul>
              </div>
              {site.whatsAppUrl ? (
                <div className="upper-column info-box whatsapp-box">
                  <div className="icon-box">
                    <i className="fa fa-whatsapp" aria-hidden="true" />
                  </div>
                  <ul>
                    <li>
                      <SmartLink href={site.whatsAppUrl} tracking="whatsapp" trackingLocation="header_whatsapp_text">
                        <strong>{t('whatsapp')}</strong>
                      </SmartLink>
                    </li>
                    <li>{site.whatsAppSubLabel}</li>
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <section className={`mainmenu-area stricky${sticky ? ' stricky-fixed fadeInDown animated' : ' slideIn animated'}`}>
        <div className="container">
          <div className="menu-column">
            <div className="row">
              <div className="col-md-8">
                <nav className="main-menu" aria-label={t('menu')}>
                  <div className="navbar-header">
                    <button
                      type="button"
                      className="navbar-toggle"
                      aria-expanded={navOpen}
                      aria-controls="kfz-main-nav"
                      aria-label={t('toggleNavigation')}
                      onClick={() => setNavOpen((o) => !o)}
                    >
                      <span className="icon-bar" />
                      <span className="icon-bar" />
                      <span className="icon-bar" />
                      <span className="icon-bar" />
                    </button>
                  </div>
                  <div id="kfz-main-nav" className={`navbar-collapse collapse clearfix${navOpen ? ' in' : ''}`}>
                    <ul className="navigation clearfix">
                      {main.map((item) => (
                        <li
                          key={item.id}
                          className={item.children.length ? 'dropdown' : undefined}
                          onMouseEnter={() => setOpenDesktop(item.id)}
                          onMouseLeave={() => setOpenDesktop((o) => (o === item.id ? null : o))}
                        >
                          <SmartLink href={item.href} newTab={item.newTab}>
                            <span aria-current={isCurrent(item, page?.path) && item.href === page?.path ? 'page' : undefined}>{item.label}</span>
                          </SmartLink>
                          {item.children.length ? (
                            <ul className={openDesktop === item.id ? 'kfz-open' : undefined}>
                              {item.children.map((child) => (
                                <li key={child.id}>
                                  <SmartLink href={child.href} newTab={child.newTab}>
                                    {child.label}
                                  </SmartLink>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                    <ul className="mobile-menu clearfix">
                      {main.map((item) => (
                        <li key={item.id} className={item.children.length ? 'dropdown' : undefined}>
                          <SmartLink href={item.href} newTab={item.newTab}>
                            {item.label}
                          </SmartLink>
                          {item.children.length ? (
                            <>
                              <ul className={openMobile[item.id] ? 'kfz-open' : undefined}>
                                {item.children.map((child) => (
                                  <li key={child.id}>
                                    <SmartLink href={child.href} newTab={child.newTab}>
                                      {child.label}
                                    </SmartLink>
                                  </li>
                                ))}
                              </ul>
                              <button
                                type="button"
                                className="dropdown-btn"
                                aria-expanded={!!openMobile[item.id]}
                                aria-label={`${t('toggleSubmenu')}: ${item.label}`}
                                onClick={() => setOpenMobile((m) => ({ ...m, [item.id]: !m[item.id] }))}
                              />
                            </>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
